import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import QRCode from 'qrcode';
import { paymentsAPI, tenantsAPI, filesAPI, fileViewUrl, leasesAPI } from '../services/api';
import Modal from '../components/common/Modal';
import { toast } from '../components/common/Toast';
import { CreditCard, Plus, Search, CheckCircle, Receipt, RefreshCw, Download, Paperclip, Edit2, Trash2, Calendar } from 'lucide-react';
import { fmt, statusClass, errMsg } from '../utils/helpers';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';

const EMPTY   = { tenantId:'', amount:'', type:'rent', method:'ACH', status:'pending', dueDate:'', paidDate:'', period:'', notes:'' };
const METHODS = ['ACH','Check','Credit Card','Cash','Money Order'];
const TYPES   = ['rent','deposit','late_fee','maintenance','other'];
const today   = () => new Date().toISOString().slice(0, 10);

function prevMonthPeriod() {
  const now = new Date();
  const y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const m = now.getMonth() === 0 ? 12 : now.getMonth();
  return `${y}-${String(m).padStart(2, '0')}`;
}

function lastDayOfPeriod(period) {
  if (!period) return '';
  const [y, m] = period.split('-').map(Number);
  return new Date(y, m, 0).toISOString().slice(0, 10);
}

function buildPaymentNote(period, amount) {
  const fmtAmt = amount ? parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
  return `Payment of period ${period} | Pago del periodo ${period} y la cantidad de: $${fmtAmt}`;
}

export default function Payments() {
  const { t } = useTranslation();
  const { user }  = useAuth();
  const { can }   = usePermissions();
  const canEdit   = user?.role !== 'tenant'; // keep for tenant-specific data/form behavior
  const canCreate = can('payments', 'create');
  const canPayEdit   = can('payments', 'edit');
  const canDelete = can('payments', 'delete');

  const [payments,     setPayments]     = useState([]);
  const [receiptUrls,  setReceiptUrls]  = useState({});
  const [tenants,      setTenants]      = useState([]);
  const [tenantLease,  setTenantLease]  = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [search,   setSearch]   = useState('');
  const [statusF,  setStatusF]  = useState('');
  const [typeF,    setTypeF]    = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [modal,       setModal]       = useState(false);
  const [receipt,     setReceipt]     = useState(null);
  const [editPayment, setEditPayment] = useState(null);
  const [editForm,    setEditForm]    = useState(EMPTY);
  const [confirmDel,  setConfirmDel]  = useState(null);
  const [form,        setForm]        = useState(EMPTY);
  const [file,        setFile]        = useState(null);
  const [saving,      setSaving]      = useState(false);
  const fileRef          = useRef();
  const managerSigRef    = useRef();
  const tenantSigRef     = useRef();
  const editManagerSigRef = useRef();
  const editTenantSigRef  = useRef();

  const getSig = (ref) => {
    const c = ref?.current;
    if (!c) return null;
    const d = c.getContext('2d').getImageData(0, 0, c.width, c.height).data;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i] < 200 && d[i + 3] > 10) return c.toDataURL('image/png');
    }
    return null;
  };

  const load = useCallback(() => {
    setLoading(true);
    const q = {};
    if (statusF)  q.status   = statusF;
    if (typeF)    q.type     = typeF;
    if (search)   q.search   = search;
    if (dateFrom) q.dateFrom = dateFrom;
    if (dateTo)   q.dateTo   = dateTo;
    const empty = Promise.resolve({ data: { data: [] } });
    const calls = canEdit
      ? [paymentsAPI.getAll(q), tenantsAPI.getAll(), empty]
      : [paymentsAPI.getAll(q), empty, leasesAPI.getAll({ status: 'active' })];
    Promise.all(calls)
      .then(([p, tdata, ldata]) => {
        const list = p.data.data;
        setPayments(list);
        setTenants(tdata.data.data);
        const leases = ldata.data.data || [];
        if (!canEdit) setTenantLease(leases.length > 0 ? leases[0] : null);
        // Pre-fetch signed URLs for receipt attachments so links are direct S3 URLs
        const withReceipt = list.filter(pay => pay.receiptUrl && !pay.receiptUrl.startsWith('/'));
        if (withReceipt.length) {
          Promise.all(
            withReceipt.map(async (pay) => {
              try {
                const res = await filesAPI.getSignedUrl(pay.receiptUrl);
                return [pay._id, res.data.url];
              } catch {
                return [pay._id, fileViewUrl(pay.receiptUrl)];
              }
            })
          ).then(entries => setReceiptUrls(Object.fromEntries(entries)));
        }
      })
      .catch(() => toast.error(t('failedToLoad')))
      .finally(() => setLoading(false));
  }, [statusF, typeF, search, dateFrom, dateTo, canEdit, t]);

  useEffect(() => { const timer = setTimeout(load, 300); return () => clearTimeout(timer); }, [load]);

  const totals = {
    collected: payments.filter(p => p.status === 'paid').reduce((s, p) => s + p.amount, 0),
    pending:   payments.filter(p => p.status !== 'paid').reduce((s, p) => s + p.amount, 0),
  };

  const openModal = () => {
    if (!canEdit && tenantLease) {
      const period = prevMonthPeriod();
      const amount = tenantLease.monthlyRent ? String(tenantLease.monthlyRent) : '';
      const dueDate = lastDayOfPeriod(period);
      const notes = buildPaymentNote(period, amount);
      setForm({ ...EMPTY, amount, period, dueDate, notes, type: 'rent' });
    } else {
      setForm(EMPTY);
    }
    setFile(null);
    setModal(true);
  };

  const handleSave = async () => {
    if (canEdit  && (!form.tenantId || !form.amount)) return toast.warning(t('tenant') + ' & ' + t('amount') + ' ' + t('required'));
    if (!canEdit && (!form.amount   || !form.dueDate)) return toast.warning(t('amount') + ' & ' + t('dueDate') + ' ' + t('required'));
    setSaving(true);
    try {
      const fd = new FormData();

      if (canEdit) {
        const tnt = tenants.find(ten => ten._id === form.tenantId);
        const extra = {
          apartmentId: tnt?.apartmentId?._id || tnt?.apartmentId,
          leaseId:     tnt?.leaseId?._id     || tnt?.leaseId,
        };
        // Merge form + extra, skip empty strings to avoid ObjectId cast errors
        Object.entries({ ...form, ...extra }).forEach(([k, v]) => {
          if (v !== '' && v !== null && v !== undefined) fd.append(k, v);
        });
      } else {
        Object.entries(form).forEach(([k, v]) => {
          if (v !== '' && v !== null && v !== undefined) fd.append(k, v);
        });
      }

      if (file) fd.append('receipt', file);
      const mSig = getSig(managerSigRef);
      const tSig = getSig(tenantSigRef);
      if (mSig) fd.append('managerSignature', mSig);
      if (tSig) fd.append('tenantSignature',  tSig);

      await paymentsAPI.create(fd);
      toast.success(t('paymentRecorded'));
      setModal(false); setForm(EMPTY); setFile(null); load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const markPaid = async p => {
    try { await paymentsAPI.markPaid(p._id, { method: p.method }); toast.success(t('markedAsPaid')); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const openEdit = p => {
    setEditForm({
      amount:   String(p.amount || ''),
      type:     p.type || 'rent',
      method:   p.method || 'ACH',
      status:   p.status || 'pending',
      dueDate:  p.dueDate  ? p.dueDate.slice(0,10)  : '',
      paidDate: p.paidDate ? p.paidDate.slice(0,10) : '',
      period:   p.period || '',
      notes:    p.notes  || '',
    });
    setEditPayment(p);
  };

  const handleEdit = async () => {
    if (!editForm.amount) return toast.warning(t('amount') + ' ' + t('required'));
    setSaving(true);
    try {
      const data = { ...editForm };
      if (!data.paidDate) delete data.paidDate;
      if (!data.dueDate)  delete data.dueDate;
      const mSig = getSig(editManagerSigRef);
      const tSig = getSig(editTenantSigRef);
      if (mSig) data.managerSignature = mSig;
      if (tSig) data.tenantSignature  = tSig;
      await paymentsAPI.update(editPayment._id, data);
      toast.success(t('paymentUpdated'));
      setEditPayment(null); load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await paymentsAPI.delete(confirmDel._id);
      toast.success(t('paymentDeleted'));
      setConfirmDel(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const generateReceipt = async (p) => {
    try {
      const { data } = await paymentsAPI.update(p._id, { status: 'paid', paidDate: p.paidDate || today() });
      toast.success(t('receiptGenerated'));
      load();
      setReceipt(data.data || { ...p, ...data });
    } catch (err) { toast.error(errMsg(err)); }
  };

  const handleTypeChange = (type) => {
    if (type === 'deposit') {
      setForm(f => ({ ...f, type, period: '', status: 'paid', dueDate: today(), paidDate: today(),
        notes: `Security deposit | Depósito de seguridad` }));
    } else if (type === 'rent') {
      const period = prevMonthPeriod();
      const dueDate = lastDayOfPeriod(period);
      setForm(f => ({ ...f, type, period, dueDate, paidDate: '', status: 'pending',
        notes: f.amount ? buildPaymentNote(period, f.amount) : '' }));
    } else {
      setForm(f => ({ ...f, type, period: '', dueDate: today(), paidDate: '', status: 'pending', notes: '' }));
    }
  };

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">{t('payments')}</h1><p className="page-subtitle">{t('rentCollectionReceipts')}</p></div>
        <div style={{ display:'flex', gap:10 }}>
          <button className="btn btn-secondary btn-icon" onClick={load}><RefreshCw size={16}/></button>
          {(canCreate || !canEdit) && <button className="btn btn-primary" onClick={openModal}><Plus size={16}/>{t('recordPayment')}</button>}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-3 mb-6">
        {[
          { label:t('collected'),       value:fmt.currency(totals.collected), color:'var(--emerald)' },
          { label:t('pendingOverdue'),  value:fmt.currency(totals.pending),   color:'var(--rose)' },
          { label:t('totalTransactions'),value:payments.length,               color:'var(--brand)' },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding:'18px 22px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <div style={{ fontSize:12,color:'var(--ink-40)',marginBottom:4,fontWeight:600,textTransform:'uppercase',letterSpacing:'.04em' }}>{s.label}</div>
              <div style={{ fontSize:26,fontWeight:800,color:s.color,fontFamily:'var(--font-display)' }}>{s.value}</div>
            </div>
            <CreditCard size={28} color={s.color} style={{ opacity:.2 }}/>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap', alignItems:'center' }}>
        <div className="search-bar" style={{ flex:'2 1 200px', minWidth:180 }}>
          <Search size={15} color="var(--ink-20)"/>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('searchTenantReceipt')}/>
        </div>
        <select className="form-input form-select" style={{ flex:'1 1 120px', minWidth:100 }} value={statusF} onChange={e => setStatusF(e.target.value)}>
          <option value="">{t('allStatus')}</option>
          {['paid','pending','overdue'].map(s => <option key={s} value={s}>{t(s)}</option>)}
        </select>
        <select className="form-input form-select" style={{ flex:'1 1 120px', minWidth:100 }} value={typeF} onChange={e => setTypeF(e.target.value)}>
          <option value="">{t('allTypes')}</option>
          {TYPES.map(type => <option key={type} value={type}>{t(type)}</option>)}
        </select>

        {/* Date range */}
        <div style={{ display:'flex', gap:6, alignItems:'center', flex:'2 1 280px', minWidth:240 }}>
          <Calendar size={15} color="var(--ink-40)" style={{ flexShrink:0 }}/>
          <span style={{ fontSize:12, color:'var(--ink-40)', whiteSpace:'nowrap' }}>{t('dateFrom')}</span>
          <input
            className="form-input"
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            style={{ flex:1, minWidth:0, fontSize:13 }}
          />
          <span style={{ fontSize:12, color:'var(--ink-40)', whiteSpace:'nowrap' }}>{t('dateTo')}</span>
          <input
            className="form-input"
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            style={{ flex:1, minWidth:0, fontSize:13 }}
          />
        </div>

        {/* Quick presets */}
        <div style={{ display:'flex', gap:6, flexShrink:0 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => {
            const now = new Date();
            setDateFrom(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0,10));
            setDateTo(new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0,10));
          }}>{t('thisMonth')}</button>
          <button className="btn btn-secondary btn-sm" onClick={() => {
            const now = new Date();
            const y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
            const m = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
            setDateFrom(new Date(y, m, 1).toISOString().slice(0,10));
            setDateTo(new Date(y, m + 1, 0).toISOString().slice(0,10));
          }}>{t('lastMonth')}</button>
          {(dateFrom || dateTo) && (
            <button className="btn btn-secondary btn-sm" onClick={() => { setDateFrom(''); setDateTo(''); }}>✕</button>
          )}
        </div>
      </div>

      <div className="card">
        <div style={{ overflowX:'auto' }}>
          {loading ? <div style={{ padding:40,textAlign:'center' }}><div className="spinner" style={{ margin:'0 auto' }}/></div> : (
            <table className="data-table">
              <thead>
                <tr><th style={{ width:1,whiteSpace:'nowrap' }}></th><th>{t('receipt')}</th><th>{t('tenant')}</th><th>{t('type')}</th><th>{t('amount')}</th><th>{t('method')}</th><th>{t('due')}</th><th>{t('paid')}</th><th>{t('status')}</th></tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p._id}>
                    <td className="table-actions"><div style={{ display:'flex', gap:6 }}>
                      {canPayEdit && p.status !== 'paid' && <button className="btn btn-success btn-sm" onClick={() => markPaid(p)}><CheckCircle size={13}/>{t('pay')}</button>}
                      {p.receiptNo && <button className="btn btn-secondary btn-sm" onClick={() => setReceipt(p)}><Receipt size={13}/>{t('receipt')}</button>}
                      {canPayEdit && p.status === 'paid' && !p.receiptNo && <button className="btn btn-secondary btn-sm" onClick={() => generateReceipt(p)}><Receipt size={13}/>{t('generateReceipt')}</button>}
                      {p.receiptUrl && (
                        <a className="btn btn-secondary btn-sm" href={receiptUrls[p._id] || fileViewUrl(p.receiptUrl) || '#'} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none' }}>
                          <Paperclip size={13}/>{t('proof')}
                        </a>
                      )}
                      {canPayEdit && <button className="btn btn-secondary btn-sm btn-icon" onClick={() => openEdit(p)} title={t('edit')}><Edit2 size={13}/></button>}
                      {canDelete && <button className="btn btn-danger btn-sm btn-icon" onClick={() => setConfirmDel(p)} title={t('delete')}><Trash2 size={13}/></button>}
                    </div></td>
                    <td><span className="font-mono text-sm" style={{ color:'var(--brand)' }}>{p.receiptNo || '—'}</span></td>
                    <td>
                      <div style={{ fontWeight:500,fontSize:13 }}>{p.tenantId?.name || p.userId?.name || '—'}</div>
                      <div style={{ fontSize:11,color:'var(--ink-40)' }}>{p.apartmentId?.propertyId?.name} #{p.apartmentId?.unit}</div>
                    </td>
                    <td><span className="badge badge-neutral">{t(p.type)}</span></td>
                    <td><span style={{ fontWeight:700 }}>{fmt.currency(p.amount)}</span></td>
                    <td style={{ fontSize:13 }}>{p.method}</td>
                    <td style={{ fontSize:12 }}>{fmt.date(p.dueDate)}</td>
                    <td style={{ fontSize:12 }}>{p.paidDate ? fmt.date(p.paidDate) : <span style={{ color:'var(--ink-20)' }}>—</span>}</td>
                    <td><span className={`badge ${statusClass(p.status)}`}>{t(p.status)}</span></td>
                  </tr>
                ))}
                {payments.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign:'center',padding:40,color:'var(--ink-40)' }}>{t('noPaymentsFound')}</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add Payment Modal */}
      <Modal open={modal} onClose={() => setModal(false)} title={t('recordPayment')}
        footer={
          <><button className="btn btn-secondary" onClick={() => setModal(false)}>{t('cancel')}</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? t('saving') : canEdit ? t('save') : t('submit')}</button></>
        }>
        <div className="form-grid form-grid-2" style={{ gap:16 }}>
          {canEdit && (
            <div className="form-group">
              <label className="form-label">{t('tenant')} *</label>
              <select className="form-input form-select" value={form.tenantId} onChange={e => setForm(f => ({ ...f, tenantId:e.target.value }))}>
                <option value="">{t('select')}</option>
                {tenants.map(ten => <option key={ten._id} value={ten._id}>{ten.name}</option>)}
              </select>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">{t('type')}</label>
            <select className="form-input form-select" value={form.type} onChange={e => handleTypeChange(e.target.value)}>
              {TYPES.map(type => <option key={type} value={type}>{t(type)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t('amountDollar')} *</label>
            <input className="form-input" type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount:e.target.value }))} placeholder="1500"/>
          </div>
          <div className="form-group">
            <label className="form-label">{t('method')}</label>
            <select className="form-input form-select" value={form.method} onChange={e => setForm(f => ({ ...f, method:e.target.value }))}>
              {METHODS.map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          {canEdit && (
            <div className="form-group">
              <label className="form-label">{t('status')}</label>
              <select className="form-input form-select" value={form.status} onChange={e => setForm(f => ({ ...f, status:e.target.value }))}>
                {['paid','pending','overdue'].map(s => <option key={s} value={s}>{t(s)}</option>)}
              </select>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">{t('periodYYYYMM')}</label>
            <input className="form-input" value={form.period} onChange={e => setForm(f => ({ ...f, period:e.target.value }))} placeholder={new Date().toISOString().slice(0,7)}/>
          </div>
          <div className="form-group">
            <label className="form-label">{t('dueDate')} *</label>
            <input className="form-input" type="date" value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate:e.target.value }))}/>
          </div>
          {canEdit && (
            <div className="form-group">
              <label className="form-label">{t('paidDate')}</label>
              <input className="form-input" type="date" value={form.paidDate} onChange={e => setForm(f => ({ ...f, paidDate:e.target.value }))}/>
            </div>
          )}
          {/* Receipt / proof of payment file upload */}
          <div className="form-group" style={{ gridColumn:'1/-1' }}>
            <label className="form-label">{t('proofOfPayment')}</label>
            <div
              style={{ border:'2px dashed var(--border)', borderRadius:8, padding:'14px 16px', cursor:'pointer', display:'flex', alignItems:'center', gap:12, background: file ? 'var(--surface-2)' : 'transparent' }}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" style={{ display:'none' }} accept=".pdf,.png,.jpg,.jpeg,.webp"
                onChange={e => setFile(e.target.files[0] || null)} />
              <Paperclip size={18} color="var(--ink-20)"/>
              {file ? (
                <div>
                  <div style={{ fontSize:13, fontWeight:600 }}>{file.name}</div>
                  <div style={{ fontSize:11, color:'var(--ink-40)' }}>{(file.size/1024).toFixed(1)} KB</div>
                </div>
              ) : (
                <span style={{ fontSize:13, color:'var(--ink-40)' }}>{t('clickToAttachReceipt')}</span>
              )}
              {file && <button type="button" style={{ marginLeft:'auto', fontSize:11, color:'var(--rose)', background:'none', border:'none', cursor:'pointer' }} onClick={e => { e.stopPropagation(); setFile(null); }}>✕</button>}
            </div>
          </div>
          <div className="form-group" style={{ gridColumn:'1/-1' }}>
            <label className="form-label">{t('notes')}</label>
            <textarea className="form-input" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes:e.target.value }))} placeholder={t('optional')}/>
          </div>
          {form.status === 'paid' && (
            <div style={{ gridColumn:'1/-1', borderTop:'1px dashed var(--border)', paddingTop:14, marginTop:4 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--ink-60)', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:4 }}>{t('signaturesRequired')}</div>
              <div style={{ fontSize:12, color:'var(--ink-40)', marginBottom:10 }}>{t('signBeforeSaving')}</div>
              <div style={{ display:'flex', gap:14 }}>
                <SignaturePad label={t('managerSignature')} canvasRef={managerSigRef}/>
                <SignaturePad label={t('tenantSignature')}  canvasRef={tenantSigRef}/>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Receipt Modal */}
      <Modal open={!!receipt} onClose={() => setReceipt(null)} title={t('officialPaymentReceipt')} size="sm"
        footer={<><button className="btn btn-secondary" onClick={() => setReceipt(null)}>{t('close')}</button><button className="btn btn-primary" onClick={() => window.print()}><Download size={14}/>{t('print')}</button></>}>
        {receipt && <ReceiptView p={receipt} t={t} receiptUrl={receiptUrls[receipt._id] || fileViewUrl(receipt.receiptUrl)}/>}
      </Modal>

      {/* Edit Payment Modal */}
      <Modal open={!!editPayment} onClose={() => setEditPayment(null)} title={t('edit') + ' ' + t('payment')}
        footer={<><button className="btn btn-secondary" onClick={() => setEditPayment(null)}>{t('cancel')}</button><button className="btn btn-primary" onClick={handleEdit} disabled={saving}>{saving ? t('saving') : t('save')}</button></>}>
        <div className="form-grid form-grid-2" style={{ gap:16 }}>
          <div className="form-group">
            <label className="form-label">{t('amountDollar')} *</label>
            <input className="form-input" type="number" value={editForm.amount} onChange={e => setEditForm(f => ({ ...f, amount:e.target.value }))}/>
          </div>
          <div className="form-group">
            <label className="form-label">{t('type')}</label>
            <select className="form-input form-select" value={editForm.type} onChange={e => setEditForm(f => ({ ...f, type:e.target.value }))}>
              {TYPES.map(tp => <option key={tp} value={tp}>{t(tp)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t('method')}</label>
            <select className="form-input form-select" value={editForm.method} onChange={e => setEditForm(f => ({ ...f, method:e.target.value }))}>
              {METHODS.map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t('status')}</label>
            <select className="form-input form-select" value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status:e.target.value }))}>
              {['paid','pending','overdue'].map(s => <option key={s} value={s}>{t(s)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t('dueDate')}</label>
            <input className="form-input" type="date" value={editForm.dueDate} onChange={e => setEditForm(f => ({ ...f, dueDate:e.target.value }))}/>
          </div>
          <div className="form-group">
            <label className="form-label">{t('paidDate')}</label>
            <input className="form-input" type="date" value={editForm.paidDate} onChange={e => setEditForm(f => ({ ...f, paidDate:e.target.value }))}/>
          </div>
          <div className="form-group">
            <label className="form-label">{t('periodYYYYMM')}</label>
            <input className="form-input" value={editForm.period} onChange={e => setEditForm(f => ({ ...f, period:e.target.value }))}/>
          </div>
          <div className="form-group" style={{ gridColumn:'1/-1' }}>
            <label className="form-label">{t('notes')}</label>
            <textarea className="form-input" rows={2} value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes:e.target.value }))}/>
          </div>
          {editForm.status === 'paid' && !editPayment?.managerSignature && (
            <div style={{ gridColumn:'1/-1', borderTop:'1px dashed var(--border)', paddingTop:14, marginTop:4 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--ink-60)', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:4 }}>{t('signaturesRequired')}</div>
              <div style={{ fontSize:12, color:'var(--ink-40)', marginBottom:10 }}>{t('signBeforeSaving')}</div>
              <div style={{ display:'flex', gap:14 }}>
                <SignaturePad label={t('managerSignature')} canvasRef={editManagerSigRef}/>
                <SignaturePad label={t('tenantSignature')}  canvasRef={editTenantSigRef}/>
              </div>
            </div>
          )}
          {editPayment?.managerSignature && (
            <div style={{ gridColumn:'1/-1', borderTop:'1px dashed var(--border)', paddingTop:14, marginTop:4 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--emerald)', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:8 }}>✓ {t('signatureSection')}</div>
              <div style={{ display:'flex', gap:14 }}>
                <SignaturePad label={t('managerSignature')} savedImage={editPayment.managerSignature}/>
                <SignaturePad label={t('tenantSignature')}  savedImage={editPayment.tenantSignature}/>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal open={!!confirmDel} onClose={() => setConfirmDel(null)} title={t('delete') + ' ' + t('payment')} size="sm"
        footer={<><button className="btn btn-secondary" onClick={() => setConfirmDel(null)}>{t('cancel')}</button><button className="btn btn-danger" onClick={handleDelete}>{t('delete')}</button></>}>
        {confirmDel && <p style={{ fontSize:14, color:'var(--ink-60)' }}>Delete payment of <strong>{fmt.currency(confirmDel.amount)}</strong> for <strong>{confirmDel.tenantId?.name || confirmDel.userId?.name}</strong>? This cannot be undone.</p>}
      </Modal>
    </div>
  );
}

// `compact` shrinks the pad for the printable receipt, where vertical space is
// tight. The add/edit forms keep the full height — it's an actual drawing
// surface there and needs the room.
function SignaturePad({ label, canvasRef: extRef, savedImage, compact = false }) {
  const internalRef = useRef();
  const ref = extRef || internalRef;
  const [drawing, setDrawing] = useState(false);
  const last = useRef(null);
  const padH = compact ? 44 : 80;

  const pos = (e, canvas) => {
    const r = canvas.getBoundingClientRect();
    const sx = canvas.width / r.width, sy = canvas.height / r.height;
    const s = e.touches ? e.touches[0] : e;
    return { x: (s.clientX - r.left) * sx, y: (s.clientY - r.top) * sy };
  };
  const start = e => { e.preventDefault(); setDrawing(true); last.current = pos(e, ref.current); };
  const move  = e => {
    if (!drawing) return; e.preventDefault();
    const ctx = ref.current.getContext('2d'), p2 = pos(e, ref.current);
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p2.x, p2.y);
    ctx.strokeStyle='#1a1a2e'; ctx.lineWidth=2; ctx.lineCap='round'; ctx.stroke();
    last.current = p2;
  };
  const end = () => setDrawing(false);
  const clear = () => { const c = ref.current; c.getContext('2d').clearRect(0,0,c.width,c.height); };

  if (savedImage) {
    return (
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:compact?9:10, color:'var(--ink-40)', marginBottom:3, fontWeight:600, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
        <img src={savedImage} alt={label} style={{ width:'100%', height:padH, objectFit:'contain', border:'1px solid var(--border)', borderRadius:compact?5:6, background:'#fafafa' }}/>
      </div>
    );
  }

  return (
    <div style={{ flex:1, minWidth:0 }}>
      <div style={{ fontSize:compact?9:10, color:'var(--ink-40)', marginBottom:3, fontWeight:600, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
      <canvas ref={ref} width={360} height={padH}
        style={{ border:'1px solid var(--border)', borderRadius:compact?5:6, cursor:'crosshair', touchAction:'none', width:'100%', height:padH, background:'#fafafa', display:'block' }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <button type="button" onClick={clear} style={{ fontSize:compact?10:11,color:'var(--rose)',background:'none',border:'none',cursor:'pointer',padding:0,marginTop:compact?1:2 }}>✕ Clear</button>
    </div>
  );
}

function ReceiptView({ p, t, receiptUrl }) {
  const [qrDataUrl, setQrDataUrl] = useState('');

  // Build a verification URL encoding receipt + HMAC token; generate QR on mount
  const verifyUrl = useMemo(() => {
    if (!p.receiptNo || !p.verifyToken) return null;
    return `https://propmanager.mercurysoftwaresolutions.com/api/payments/verify?r=${encodeURIComponent(p.receiptNo)}&s=${encodeURIComponent(p.verifyToken)}`;
  }, [p.receiptNo, p.verifyToken]);

  useEffect(() => {
    if (!verifyUrl) return;
    QRCode.toDataURL(verifyUrl, { width: 112, margin: 1, color: { dark: '#1a1a2e', light: '#f8f9fa' } })
      .then(setQrDataUrl)
      .catch(() => {});
  }, [verifyUrl]);

  return (
    <div style={{ fontFamily:'var(--font-mono)', background:'var(--surface-2)', borderRadius:12, padding:'14px 16px', border:'1px solid var(--border)' }}>

      {/* Header — brand + building name */}
      <div style={{ textAlign:'center', marginBottom:10 }}>
        <div style={{ fontFamily:'var(--font-display)', fontSize:17, color:'var(--brand)', lineHeight:1.1 }}>PropManager</div>
        <div style={{ fontSize:11, fontWeight:700, color:'var(--ink-80)', letterSpacing:'.08em', marginTop:1 }}>MERCURY</div>
        <div style={{ fontSize:8.5, color:'var(--ink-40)', letterSpacing:'.1em', marginTop:3 }}>
          {p.type === 'deposit' ? 'SECURITY DEPOSIT RECEIPT' : t('officialPaymentReceipt')}
        </div>
      </div>

      {/* Fields */}
      <div style={{ borderTop:'2px dashed var(--border)', borderBottom:'2px dashed var(--border)', padding:'9px 0', marginBottom:9 }}>
        {[
          [t('receiptNumber'), p.receiptNo],
          [t('date'),          fmt.date(p.paidDate)],
          [t('tenant'),        p.tenantId?.name || p.userId?.name],
          [t('unit'),          '# ' + p.apartmentId?.unit],
          [t('period'),        p.period || '—'],
          [t('type'),          t(p.type)],
          [t('method'),        p.method],
        ].map(([k, v]) => (
          <div key={k} style={{ display:'flex', justifyContent:'space-between', gap:10, fontSize:11.5, lineHeight:1.5, marginBottom:1 }}>
            <span style={{ color:'var(--ink-40)' }}>{k}</span>
            <span style={{ fontWeight:600, textAlign:'right' }}>{v || '—'}</span>
          </div>
        ))}
      </div>

      {/* Amount */}
      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:8.5, color:'var(--ink-40)', letterSpacing:'.08em', marginBottom:1 }}>{t('amountPaid')}</div>
        <div style={{ fontSize:26, fontWeight:900, color:'var(--emerald)', fontFamily:'var(--font-display)', lineHeight:1.15 }}>{fmt.currencyFull(p.amount)}</div>
      </div>

      {/* Attachment link */}
      {p.receiptUrl && receiptUrl && (
        <div style={{ textAlign:'center', marginTop:6 }}>
          <a href={receiptUrl} target="_blank" rel="noopener noreferrer"
            style={{ fontSize:11, color:'var(--brand)', textDecoration:'none', display:'inline-flex', alignItems:'center', gap:4 }}>
            <Paperclip size={11}/>{t('viewAttachment')}
          </a>
        </div>
      )}

      {p.notes && <div style={{ marginTop:6, fontSize:11, color:'var(--ink-40)', textAlign:'center' }}>{p.notes}</div>}

      {/* QR security seal */}
      {qrDataUrl && (
        <div style={{ marginTop:9, paddingTop:9, borderTop:'1px dashed var(--border)', display:'flex', alignItems:'center', gap:10 }}>
          <img src={qrDataUrl} alt="QR verify" style={{ width:56, height:56, borderRadius:5, border:'1px solid var(--border)', flexShrink:0 }}/>
          <div style={{ fontSize:9, color:'var(--ink-40)', lineHeight:1.45, minWidth:0 }}>
            <div style={{ fontWeight:700, fontSize:9.5, color:'var(--ink-60)', marginBottom:2, textTransform:'uppercase', letterSpacing:'.05em' }}>Verificación digital</div>
            <div>Escanee el código QR para verificar la autenticidad de este recibo.</div>
            <div style={{ marginTop:2, fontFamily:'var(--font-mono)', fontSize:8, color:'var(--ink-20)', wordBreak:'break-all' }}>
              SIG: {p.verifyToken}
            </div>
          </div>
        </div>
      )}
      {!qrDataUrl && !verifyUrl && (
        <div style={{ marginTop:8, fontSize:9, color:'var(--ink-20)', textAlign:'center' }}>Sin firma digital (recibo legado)</div>
      )}

      {/* Signature section */}
      <div style={{ marginTop:9, paddingTop:9, borderTop:'1px dashed var(--border)' }}>
        <div style={{ fontSize:9, fontWeight:700, color:'var(--ink-60)', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:5 }}>{t('signatureSection')}</div>
        <div style={{ display:'flex', gap:10 }}>
          <SignaturePad compact label={t('managerSignature')} savedImage={p.managerSignature || null}/>
          <SignaturePad compact label={t('tenantSignature')}  savedImage={p.tenantSignature  || null}/>
        </div>
      </div>

      <div style={{ textAlign:'center', marginTop:8, fontSize:9, color:'var(--ink-20)' }}>{t('thankYouReceipt')}</div>
    </div>
  );
}
